'use client';

/* eslint-disable no-console */
import { useCallback, useEffect, useState, useRef } from 'react';

import type { AdsSections } from '../types/ads';

import { useIsDesktop } from './useIsDesktop';

import { isTrackingAllowed } from '@/utils/nativeBridge';

interface GoogleEvent {
  slot: any;
  advertiserId: any;
  campaignId: any;
  creativeId: any;
  isEmpty: any;
  lineItemId: any;
  size: any;
  sourceAgnosticCreativeId: any;
  sourceAgnosticLineItemId: any;
}

declare global {
  interface Window {
    googletag: any;
  }
}

interface GoogleAdProps {
  adsSections: AdsSections;
}

const ID_AGEA = 242767168;

export const useGoogleAds = ({ adsSections }: GoogleAdProps) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isAdsInitialized, setIsAdsInitialized] = useState(false);
  const slotsRef = useRef<any[]>([]);
  const isDefiningSlots = useRef(false);
  const eventListenerRef = useRef<((event: GoogleEvent) => void) | null>(null);
  const { isMobile } = useIsDesktop();

  // ✅ Clean up existing slots
  const destroyExistingSlots = useCallback(() => {
    if (window.googletag && slotsRef.current.length > 0) {
      log('Destroying existing slots');
      window.googletag.cmd.push(() => {
        window.googletag.destroySlots(slotsRef.current);
        slotsRef.current = [];
      });
    }
  }, []);

  // ✅ event listener stable
  const handleSlotRenderEnded = useCallback((event: GoogleEvent) => {
    const slot = event.slot;
    console.groupCollapsed(
      'Slot',
      slot.getSlotElementId(),
      'finished rendering.'
    );
    log('Advertiser ID:', event.advertiserId);
    log('Campaign ID: ', event.campaignId);
    log('Creative ID: ', event.creativeId);
    log('Is empty?:', event.isEmpty);
    log('Line Item ID:', event.lineItemId);
    log('Size:', event.size);
    log('Source Agnostic Creative ID:', event.sourceAgnosticCreativeId);
    log('Source Agnostic Line Item ID:', event.sourceAgnosticLineItemId);
    console.groupEnd();
  }, []);

  // ✅ Config definitions of slots and mapping
  const defineSlots = useCallback(() => {
    if (
      isDefiningSlots.current ||
      !window.googletag ||
      !window.googletag.defineSlot
    ) {
      log('Skipping slot definition - not ready or already defining');
      return;
    }

    isDefiningSlots.current = true;
    log('Defining slots for section:', adsSections);

    const footerBox = isMobile ? ['caja1'] : ['caja1', 'caja2', 'caja3'];

    window.googletag.cmd.push(() => {
      try {
        // ✅ First destroy any existing slots
        if (slotsRef.current.length > 0) {
          window.googletag.destroySlots(slotsRef.current);
          slotsRef.current = [];
        }

        const slotDefinitions = [
          {
            name: 'horizontal1',
            elementId: 'div-gpt-ad-horizontal1',
            sizes: [
              [320, 50],
              [320, 90],
              [320, 100],
              [728, 90],
              [950, 200],
              [728, 90],
              [950, 90],
              [970, 200],
              [950, 100],
              [970, 100],
              [1920, 200],
            ],
            mapping: window.googletag
              .sizeMapping()
              .addSize(
                [1024, 0],
                [
                  [728, 90],
                  [950, 200],
                  [950, 90],
                  [970, 200],
                  [970, 100],
                  [950, 100],
                  [1920, 200],
                ]
              )
              .addSize([750, 0], [728, 90])
              .addSize(
                [0, 0],
                [
                  [320, 50],
                  [320, 90],
                  [320, 100],
                ]
              )
              .build(),
          },
          ...footerBox.map((name) => ({
            name,
            elementId: `div-gpt-ad-${name}`,
            sizes: [[300, 250], [300, 600], 'fluid'],
            mapping: window.googletag
              .sizeMapping()
              .addSize([1024, 0], [300, 250])
              .addSize([0, 0], [[300, 250], [300, 600], 'fluid'])
              .build(),
          })),
        ];

        // ✅ Dinamic slots definition
        const slots = slotDefinitions
          .map((cfg) => {
            const slot = window.googletag.defineSlot(
              `/${ID_AGEA}/grandt/${adsSections}/${cfg.name}`,
              cfg.sizes,
              cfg.elementId
            );

            if (!slot) {
              logError(`Failed to define slot for ${cfg.elementId}`);
              return null;
            }

            return slot
              .addService(window.googletag.pubads())
              .defineSizeMapping(cfg.mapping);
          })
          .filter(Boolean);

        slotsRef.current = slots;

        if (slots.length > 0) {
          log(`Successfully defined ${slots.length} slots`);
          window.googletag.pubads().refresh(slots);
        }
      } catch (error) {
        logError('Error defining slots:', error);
      } finally {
        isDefiningSlots.current = false;
      }
    });
  }, [adsSections, isMobile]);

  // ✅ Función para remover event listener
  const removeEventListener = useCallback(() => {
    if (window.googletag && eventListenerRef.current) {
      window.googletag.cmd.push(() => {
        window.googletag
          .pubads()
          .removeEventListener('slotRenderEnded', eventListenerRef.current);
        eventListenerRef.current = null;
      });
    }
  }, []);

  // ✅ Initialize Google Ads with configuration
  const initializeAds = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        if (!window.googletag) {
          reject(new Error('googletag not available'));
          return;
        }

        window.googletag.cmd.push(() => {
          try {
            log('Google Ads initializing');

            // ✅ Remover listener existente antes de agregar uno nuevo
            if (eventListenerRef.current) {
              window.googletag
                .pubads()
                .removeEventListener(
                  'slotRenderEnded',
                  eventListenerRef.current
                );
            }

            // Configure page-level settings
            const pageConfig = {
              allowOverlayExpansion: true,
              allowPushExpansion: true,
              sandbox: true,
            };

            // ✅ Guardar referencia del listener y agregarlo
            eventListenerRef.current = handleSlotRenderEnded;
            window.googletag
              .pubads()
              .addEventListener('slotRenderEnded', eventListenerRef.current);

            // ✅ Configure publisher settings
            window.googletag.pubads().setSafeFrameConfig(pageConfig);
            window.googletag.pubads().disableInitialLoad();
            window.googletag.pubads().enableSingleRequest();
            window.googletag.pubads().collapseEmptyDivs();
            // App iOS sin permiso ATT: anuncios no personalizados
            if (!isTrackingAllowed()) {
              window.googletag
                .pubads()
                .setPrivacySettings({ nonPersonalizedAds: true });
            }
            window.googletag.enableServices();

            setIsAdsInitialized(true);
            resolve();
          } catch (error) {
            logError('Error in ads initialization:', error);
            reject(error);
          }
        });
      }),
    [handleSlotRenderEnded] // ✅ Agregar dependencia estable
  );

  const loadScriptAndInitialize = useCallback(
    () =>
      new Promise<boolean>((resolve, reject) => {
        log('Loading Script Google Ads');
        // ✅ Check if script is already loaded
        if (window.googletag && window.googletag.defineSlot) {
          setIsScriptLoaded(true);
          resolve(true);
          return;
        }

        if (!document.getElementById('google-ads-script')) {
          const script = document.createElement('script');
          script.id = 'google-ads-script';
          script.async = true;
          script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

          script.onload = () => {
            // ✅ Initialize googletag if not already done
            window.googletag = window.googletag || { cmd: [] };
            setIsScriptLoaded(true);
            resolve(true);
          };

          script.onerror = () => {
            console.debug('Failed to load Google Ads script');
            reject(new Error('Failed to load Google Ads script'));
          };

          document.head.appendChild(script);
        } else {
          setIsScriptLoaded(true);
          resolve(true);
        }
      }),
    []
  );

  const initAds = useCallback(async () => {
    try {
      await loadScriptAndInitialize();
      await initializeAds();
      // ✅ Only define slots after everything is properly initialized
      defineSlots();
    } catch (error) {
      // In production, we might want to log this to an error tracking service
      logError('Error initializing ads:', error);
    }
  }, [loadScriptAndInitialize, initializeAds, defineSlots]);

  // ✅ Load the GPT script
  useEffect(() => {
    initAds();

    return () => {
      removeEventListener();
      destroyExistingSlots();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Array vacío - solo se ejecuta al montar

  // ✅ Re-define slots when adsSections changes
  useEffect(() => {
    if (isScriptLoaded && isAdsInitialized && adsSections) {
      defineSlots();
    }
  }, [adsSections, isScriptLoaded, isAdsInitialized, defineSlots]);

  return {
    isReady: isScriptLoaded && isAdsInitialized,
    destroySlots: destroyExistingSlots,
  };
};
const ENABLE_LOGGING =
  localStorage.getItem('enableGoogleAdsLogging') === 'true';

function log(...args: any[]) {
  if (process.env.NODE_ENV !== 'development' || ENABLE_LOGGING) {
    console.log(...args);
  }
}

function logError(...args: any[]) {
  if (process.env.NODE_ENV !== 'development' || ENABLE_LOGGING) {
    console.log(...args);
  }
}
